import { FormatNumberPipe } from '../../../../../shared/pipes/number-separator.pipe';
import { EntityPopup, PopupInfoItem } from './entity-popup.interface';

export interface ParcelaProperties {
  partner: string;
  municipio: string;
  polygon_number: string;
  plot_number: string;
  ref_catastral: string;
  sup_total_m2: number;
  name: string;
  hidrante: {
    hidrante_name: string;
    hidrante_state: 'active' | 'inactive' | 'without_counter';
  };
  water_socket: {
    water_socket_name: string;
    water_socket_state: 'active' | 'inactive' | 'without_counter';
  };
}

export interface ParcelaFeature {
  properties: ParcelaProperties;
}

export interface HidranteTomaStatus {
  name: string;
  status: string;
}

export class ParcelaPopup implements EntityPopup {
  private formatNumber = new FormatNumberPipe();
  private hidrantes: HidranteTomaStatus[] = [];
  private tomas: HidranteTomaStatus[] = [];

  constructor(private entities: ParcelaFeature[]) {
    this.processHidrantesAndTomas();
  }

  private processHidrantesAndTomas(): void {
    this.entities.forEach(parcela => {
      this.addHidrante(parcela.properties.hidrante);
      this.addToma(parcela.properties.water_socket);
    });
  }

  private getStateMessage(
    state: 'active' | 'inactive' | 'without_counter'
  ): string {
    const stateMap: Record<string, string> = {
      active: 'Conectado',
      inactive: 'Sin conexión',
      without_counter: 'Sin contador inteligente',
    };
    return stateMap[state] || '';
  }

  private addHidrante(hidrante: ParcelaProperties['hidrante']): void {
    if (!hidrante?.hidrante_name) return;
    if (this.hidrantes.some(e => e.name === hidrante.hidrante_name)) return;

    this.hidrantes.push({
      name: hidrante.hidrante_name,
      status: this.getStateMessage(hidrante.hidrante_state),
    });
  }

  private addToma(waterSocket: ParcelaProperties['water_socket']): void {
    if (!waterSocket?.water_socket_name) return;
    if (this.tomas.some(e => e.name === waterSocket.water_socket_name)) return;

    this.tomas.push({
      name: waterSocket.water_socket_name,
      status: this.getStateMessage(waterSocket.water_socket_state),
    });
  }

  getBadgeText(): string {
    return '';
  }

  getBadgeItems(): {
    partner: string;
    location: string;
    refCatastral: string;
  }[] {
    return this.entities.map(parcela => ({
      partner: `Socio ${parcela.properties.partner || '-'}`,
      location: `Municipio ${parcela.properties.municipio || '-'} / Polígono ${parcela.properties.polygon_number || '-'} / Parcela ${parcela.properties.plot_number || '-'}`,
      refCatastral: `Ref. Cat ${parcela.properties.ref_catastral || '-'} / Superficie ${
        parcela.properties.sup_total_m2 % 1 === 0
          ? this.formatNumber.transform(parcela.properties.sup_total_m2) + ' m²'
          : this.formatNumber.transform(parcela.properties.sup_total_m2) +
              ' m²' || '-'
      }`,
    }));
  }

  getTitle(): string {
    const titles = [
      ...new Set(this.entities.map(p => p.properties.name)),
    ].filter(Boolean);
    return `Parcela ${titles.join(' / ')}`;
  }

  getHidrantes(): HidranteTomaStatus[] {
    return this.hidrantes;
  }

  getTomas(): HidranteTomaStatus[] {
    return this.tomas;
  }

  getInfoItems(): PopupInfoItem[] {
    return [
      {
        icon: 'fire_hydrant',
        label: 'Hidrante',
        value:
          this.hidrantes.length > 0
            ? this.hidrantes.map(h => `${h.name} - ${h.status}`).join(', ')
            : 'No disponible',
      },
      {
        icon: 'valve',
        label: 'Toma',
        value:
          this.tomas.length > 0
            ? this.tomas.map(t => `${t.name} - ${t.status}`).join(', ')
            : 'No disponible',
      },
    ];
  }
}
